import { isoZ, z } from 'my-zod'
import { ProgramTypeEnum, StateShortNameList } from '../../internal'
import { corporateFleetDispositionStatusEnum, corporateFleetLocations, corporateFleetSiteEnum } from '../corporate-fleet'
import { corporateFleetStatusEnum } from '../corporate-fleet/corporate-fleet-status-enum'

export const vehicleLifecycleStatusEnum = z.enum([
  'ActiveAtOperator',
  'Disposed',
  'Ordered',
  'EnrouteToOutfitter',
  'EnrouteToDealer',
  'EnrouteToWarehouse',
  'InactiveAtOperator',
  'InactiveReadyForDisposition',
  'InactiveAtNPA',
  // Used when someone is hurt on a vehicle - vehicle preserved for evidence
  'Quarantined',
  'PDIInProgress',
  'DispositionApprovedReadyForPickup',
  'InactiveDispositionReadyForReview',
  'InactivePickupScheduled',
  'Ready',
  'Incoming',
  'Arrived',
  'SalePending',
  'SaleProcessed',
  'CorporateFleet' //for now, we are going to have a separate field to track the status of corporate fleet vehicles.  This lifecycle status provides the hint that we should use that other status field
])
export type LifecycleStatus = z.infer<typeof vehicleLifecycleStatusEnum>
export const LifecycleStatuses = vehicleLifecycleStatusEnum.Values
export const LifecycleStatusOptions = Object.values(vehicleLifecycleStatusEnum.Values)

export const vehicleRegistrationStatusEnum = z.enum([
  'None',
  'Safety Stock',
  'New Vin',
  'MSO Requested - Outfitter',
  'MSO Shipped to Outfitter',
  'Registration Complete: Title Not Required',
  'Registration Complete: Title Processing',
  'Registration Complete: Titles Received',
  'Sale Pending',
  'Documentation to Fleet/Sale Complete'
])
export type RegistrationStatus = z.infer<typeof vehicleRegistrationStatusEnum>
export const RegistrationStatuses = vehicleRegistrationStatusEnum.Values

export const vehicleBillingPlanEnum = z.enum([
  'Finance',
  'Rev Share',
  'Marketing',
  'Not Adv Owned',
  'Select',
  'Seasonal Holdover'
])
export type BillingPlan = z.infer<typeof vehicleBillingPlanEnum>
export const BillingPlans = vehicleBillingPlanEnum.Values

export const vehicleTelemetryProviderEnum = z.enum(['Linxup', 'PolarisCVC', 'Unsupported'])

export const vehicleLifecycleSubstatusEnum = {
  [LifecycleStatuses.InactiveAtOperator]: [
    'Damage Waiver - Repair at Dealer',
    'Damage Waiver Repair at Outfitter',
    'Service Bulletin',
    'Safety Bulletin',
    'Holdover - At Outfitter',
    'Holdover - At Upfit',
    'Between Seasons - At Outfitter',
    'Stolen',
    'Off Contract Waiting Repairs',
    'Registration Pending',
    'Marketing',
    'Tread Depth',
    'Contract Violation',
    'Total Loss Sell As-Is',
    'Total Loss Scrap'
  ],
  [LifecycleStatuses.Disposed]: [
    'Sold As-Is',
    'Scrap',
    'Stolen',
    'Previously Quarantined - sold as-is',
    'Previously Quarantined - scrapped'
  ],
  [LifecycleStatuses.EnrouteToWarehouse]: ['Reconditioning', 'New'],
  [LifecycleStatuses.InactiveReadyForDisposition]: ['New', 'Rejected', 'Unknown Condition'],
  [LifecycleStatuses.PDIInProgress]: [
    'Reconditioning Repair in Process',
    'Reconditioning RO In Process',
    'New',
    'DamageRepair',
    'WaitingOnParts'
  ],
  [LifecycleStatuses.SalePending]: [
    'Submitted to Sales Ops',
    'Processing Error',
    'Finance Ready',
    'Total Loss Sell As-Is',
    'Total Loss Scrap',
    'Unknown Condition'
  ],
  [LifecycleStatuses.ActiveAtOperator]: [
    'Tread Depth',
    'Service Bulletin',
    'Safety Bulletin',
    'In Contract',
    'Community Service',
    'Surge Fleet',
    'Marketing'
  ],
  [LifecycleStatuses.InactiveDispositionReadyForReview]: ['Account Manager', 'Disposition', 'Unknown Condition'],
  [LifecycleStatuses.DispositionApprovedReadyForPickup]: ['Unknown Condition'],
  [LifecycleStatuses.InactiveAtNPA]: ['Unknown Condition'],
  [LifecycleStatuses.InactivePickupScheduled]: ['Unknown Condition'],
  [LifecycleStatuses.Ready]: ['Unknown Condition'],
  [LifecycleStatuses.SaleProcessed]: ['Unknown Condition']
}

const lifecycleSubstatus = z.enum(Object.values(vehicleLifecycleSubstatusEnum).flat() as [string, ...string[]])

export const vehiclesModel = z.object({
  id: z.number(),
  shortId: z.string(),
  operatorId: z.number().nullable(),
  modelId: z.number().nullable(),
  customAssetFamilyId: z.number().nullable(),
  vin: z.string().max(25).nullable(),
  outfitterCustomAssetSerialNumber: z.string().max(25).nullable(),
  outfitterBeaconNumber: z.string().max(45).nullable(),
  operatorUnitNumber: z.string().max(25).nullable(),
  contractedPickupDate: isoZ.date().nullable(),
  contractedArrivalDate: isoZ.date().nullable(),
  lifecycleStatus: vehicleLifecycleStatusEnum,
  lifecycleSubstatus: lifecycleSubstatus.nullable(),
  shippedDate: isoZ.date().nullable(),
  vehicleImei: z.string().max(20).nullable(),
  telemetryProvider: vehicleTelemetryProviderEnum.nullable(),
  actualArrivalDate: isoZ.date().nullable(),
  expectedSaleDate: isoZ.date().nullable(),
  registrationNumber: z.string().max(45).nullable(),
  registrationStatus: vehicleRegistrationStatusEnum.nullable(),
  registrationFolder: z.string().max(65).nullable(),
  registrationDocumentType: z.enum(['MSO', 'Title']).nullable(),
  registrationExpirationDate: isoZ.date().nullable(),
  licensePlateNumber: z.string().max(45).nullable(),
  licenseExpirationDate: isoZ.date().nullable(),
  privateNotes: z.string().nullable(),
  saleDate: isoZ.date().nullable(),
  programType: ProgramTypeEnum,
  billingPlan: vehicleBillingPlanEnum.nullable(),
  netSaleRevenue: z.number().gte(0).nullable(),
  conditionScore: z.number().nullable(),
  conditionReportPdfUrl: z.string().nullable().or(z.string().url()).setLabel('End of Life Documents'),
  warehouseId: z.number().nullable(),
  landingPageEnabled: z.boolean(),
  buyerId: z.number().nullable(),
  quarantinedExpirationDate: isoZ.date().nullable(),
  listingPrice: z.number().nullable(),
  listingNotes: z.string().nullable(),
  serverLastModifiedDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant(),
  deletedDate: isoZ.instant().nullable(),
  expectedPickupDate: isoZ.date().nullable(),
  engineSerialNumber: z.string().max(25).nullish(),
  titleState: z.enum(StateShortNameList as [string]).nullable(),
  // Fields with the "corporateFleet" prefix are those that pertain specifically to the corporate fleet use case.
  // In some cases, we will still update this data based on data received from IBMi, but from there it will be editable in Arrival
  // For these, consider Arrival the source of truth

  // This is the Department Manager designated by the department’s senior director / manager to have working level responsibility for the department’s tracked units
  corporateFleetResponsibleUser: z.string().nullish(),

  // Project Numbers can be captured at the time of COM Order placement, editable through the unit’s lifetime if transferred between projects, to promote project-level accountability for closing YLDs
  corporateFleetProjectNumber: z.string().nullish(),

  // Fields with the "ibmi" prefix are taken from a legacy system within Polaris and represent state that system is tracking about this vehicle.
  // For these, consider IBMi the source of truth
  // (note: IBMi is the system that runs all Polaris central vehicle management. It is considered the source of truth for most vehicle data within Polaris)

  // The order number within Polaris used to order a Polaris-built VIN
  ibmiOrderNumber: z.string().nullish(),

  // For POU, this is the Polaris email of the employee who has possession of the vehicle
  ibmiAssignedEmployeeEmail: z.string().nullish(),

  // For POU, this is the full name of the employee who has possession of the vehicle
  ibmiAssignedEmployeeName: z.string().nullish(),

  // For POU, this is the Polaris employee number of the employee who has possession of the vehicle
  ibmiAssignedEmployeeNumber: z.string().nullish(),

  // For POU, this describes the state of the vehicle in its lifecycle of possession by the employee
  ibmiEmployeeUnitStatus: z.string().nullish(),

  // is an unofficial catch all for any reason why a unit may or may not be shippable out of current location
  ibmiIsShippable: z.boolean().nullish(),

  // Stands for "Yellow Label Disclaimer". Indicates a unit that is not EPA certified and therefore must be monitored
  ibmiYldHoldExists: z.boolean().nullish(),

  // unofficial catch all for any miscellaneous hold is on the vehicle (electric charge, credit, other)
  ibmiHoldExists: z.boolean().nullish(),

  // indicates an open safety bulletin is applied to the VIN and has not been closed
  ibmiSafetyBulletinExists: z.boolean().nullish(),

  // Is non-EPA certified units that are owned by Polaris under the EPA Manufacturer-Owned Exemption.
  // They must have the yellow “ Manufactured Owned Exemption ” Label or equivalent for motorcycles. These labels are also known as YLD labels
  ibmiIsPrecertified: z.boolean().nullish(),

  // Is competitive brand product owned by Polaris
  ibmiIsCompetitor: z.boolean().nullish(),

  // current customer # in which the VIN will ship to
  ibmiShippingDestination: z.string().nullish(),

  // Identifier of the Polaris Internal department
  ibmiDepartmentNumber: z.string().nullish(),

  // Identifier of the Polaris Internal customer (used for vehicle allocation and billing)
  ibmiCustomerNumber: z.string().nullish(),

  // this field describes the customer number (either an internal department or a true external customer) the vehicle is sold to
  ibmiSoldToCustomerNumber: z.string().nullish(),

  // this field describes the customer name (either an internal department or a true external customer) the vehicle is sold to
  ibmiSoldToCustomerName: z.string().nullish(),

  //stands for engineering identification number; this is an identifier given to specific vehicles which are hand-built and used to track testing, project usage, etc
  ibmiEin: z.string().nullish(),
  //Overarching Polaris facility the unit is located at
  corporateFleetIbmiSite: corporateFleetSiteEnum.nullish(),
  //A subset to the Site to provide more detail of where at that facility it can be (such as test track, yard, etc.) Not all sites have sub-locations associated to them
  corporateFleetIbmiLocation: corporateFleetLocations.nullish(),
  //this field describes the vehicles status in context of the corporate fleet use case
  corporateFleetStatus: corporateFleetStatusEnum.nullish(),
  //this field acts as a substatus for ibmiStatus, specifically only when the ibmiStatus = 'Dispositioned'
  corporateFleetDispositionStatus: corporateFleetDispositionStatusEnum.nullish(),
  lastPhysicalInventoryDate: isoZ.date().nullable()
})

export type vehiclesModel = z.infer<typeof vehiclesModel>
